import React, { Fragment, useCallback, useContext, useMemo } from 'react';
import { Skeleton } from '@material-ui/lab';
import { Tooltip, Collapse, IconButton } from '@material-ui/core';
import { ExpandMoreRounded as ExpandMoreRoundedIcon } from '@material-ui/icons';
import { EMAIL_MESSAGE, EXPAND_MORE_ICON_CLASS } from '../../../constants/patientChart.constant';
import { SALESFORCE_NOT_CONFIGURED_MESSAGE } from '../../../constants/emails.constant';
import PatientChartContext from '../../../contexts/patientChart';
import { removeXSSAttacks } from '../../../common/helper';
import { FEATURE } from '../../../utils/feature';

const hasFeature = FEATURE();

const EmailsHeading = () => (
  <div
    className={`
      flex align-items-center
      justify-content-flex-start
      height-38
      p-l-3
      p-r-3
      bg-lighter-grey
      sticky
    `}
  >
    <div className='flex flex-fill align-items-center'>
      <div className='min-width-30'></div>
      <div className='min-width-165 max-width-165 font-roboto font-14 m-r-2'>Date</div>
      <div className='min-width-120 max-width-120 font-roboto font-14 m-r-2'>To</div>
      <div className='min-width-120 max-width-120 font-roboto font-14 m-r-2'>From</div>
      <div className='email-message-subject-header font-roboto font-14'>Subject</div>
    </div>
  </div>
);

const EmailUI = ({
  Id = '',
  EntryDate = '',
  SenderName = '',
  RecipientName = '',
  Subject = '',
  Message = '',
  backgroundClass = '',
}) => {
  const { collapsible, handleExpandMultiple, handleCollapseMultiple } = useContext(PatientChartContext);

  const handleToggle = ({ key }) => {
    const request = { keys: [key] };
    if (!collapsible.has(key)) {
      handleExpandMultiple(request);
    } else {
      handleCollapseMultiple(request);
    }
  };

  const message = useMemo(() => {
    if (!Message) {
      return '';
    }

    return Message.replace(/(\n)/gmi, '<br>');
  }, [Message]);

  return (
    <Fragment>
      <div className={`
        flex
        align-items-center
        justify-content-flex-start
        height-38
        p-l-3
        p-r-3
        m-t-2
        bg-lighter-grey
        ${backgroundClass}
      `}>
        <div className='flex flex-fill align-items-center'>
          <div className='min-width-30'>
            <IconButton size='small' onClick={handleToggle.bind(this, { key: Id })}>
              <ExpandMoreRoundedIcon fontSize='small' className={EXPAND_MORE_ICON_CLASS.get(collapsible.has(Id))} />
            </IconButton>
          </div>
          
          <Tooltip title={EntryDate} placement='top' arrow>
            <div className='min-width-165 max-width-165 font-roboto font-14 m-r-2 overflow-ellipsis'>{EntryDate}</div>
          </Tooltip>
  
          <Tooltip title={RecipientName} placement='top' arrow>
            <div className='min-width-120 max-width-120 font-roboto font-14 m-r-2 overflow-ellipsis'>{RecipientName}</div>
          </Tooltip>
  
          <Tooltip title={SenderName} placement='top' arrow>
            <div className='min-width-120 max-width-120 font-roboto font-14 m-r-2 overflow-ellipsis'>{SenderName}</div>
          </Tooltip>
  
          <Tooltip title={Subject} placement='top' arrow>
            <div className='email-message-subject-cell font-roboto font-14 m-r-2 overflow-ellipsis'>{Subject}</div>
          </Tooltip>
        </div>
      </div>
  
      <Collapse in={collapsible.has(Id)} timeout='auto' unmountOnExit>
        <div
          className={`
            flex
            flex-column
            align-items-flex-start
            p-l-6
            p-r-3
            p-t-3
            p-b-3
            bg-lighter-grey
            ${backgroundClass}
          `}
          dangerouslySetInnerHTML={{
            __html: removeXSSAttacks(message),
          }}
        />
      </Collapse>
    </Fragment>
  );
}

export const Emails = ({
  tabKey = '',
  selectedKey = '',
  data: {
    emailMessages = {
      api: {
        called: false,
        loading: false,
        data: [EMAIL_MESSAGE()],
      },
    },
  },
}) => {
  const { api: { called, loading, data: emails } } = emailMessages;

  const backgroundClass = useCallback((index = 0) => {
    let className = '';
    if (index % 2 === 0) {
      className = 'bg-lightest-primary';
    }
    return className;
  }, []);

  const Loading = (
    <div className='flex flex-column flex-fill'>
      <EmailsHeading />
      <div className={`
        flex
        align-items-center
        justify-content-space-between
        height-38
        p-l-3
        p-r-3
        ${backgroundClass(0)}
      `}>
        <div className='flex flex-fill align-items-center'>
          <div className='min-width-30'></div>
          <Skeleton variant='text' className='width-165 m-r-2' />
          <Skeleton variant='text' className='width-120 m-r-2' />
          <Skeleton variant='text' className='width-120 m-r-2' />
          <div className='email-message-subject-cell align-items-center justify-content-space-between'>
            <Skeleton variant='text' className='width-120' />
          </div>
        </div>
      </div>
    </div>
  );

  const Emails = (
    <div className='flex flex-column flex-fill'>
      <EmailsHeading />

      {
        emails.map(({ Id, ...props }, index) => (
          <EmailUI
            key={Id}
            Id={Id}
            backgroundClass={backgroundClass(index)}
            {...props}
          />
        ))
      }
    </div>
  );

  const NoEmails = (
    <div className='flex flex-column flex-fill'>
      <EmailsHeading />
      <div
        className={`
          flex
          flex-column
          align-items-flex-start
          justify-content-center
          in-progress
          p-l-3
          p-r-3
        `}
      >
        <div className='flex align-items-center height-40 font-roboto font-14'>No email for this patient.</div>

        {
          !hasFeature.IS_SALESFORCE_CONFIGURED && (
            <div className='flex align-items-center height-40 font-roboto font-14 color-red-v1'>{SALESFORCE_NOT_CONFIGURED_MESSAGE}</div>
          )
        }
      </div>
    </div>
  );

  const Component = new Map([
    ['true-false-true', Emails],
    ['true-false-false', NoEmails],
    ['false-false-false', NoEmails],
  ]);

  const displayClass = tabKey !== selectedKey ? 'd-none' : '';

  return (
    <div className={`flex flex-column flex-fill column-content scroll-y-auto ${displayClass}`}>
      {Component.get(`${called}-${loading}-${!!emails.length}`) || Loading}
    </div>
  );
};

export default Emails;
